import type { CurrentUser } from '@btsis/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, use, useMemo } from 'react';
import { api } from './api.js';

export type LoginResult =
  | { status: 'success' }
  | { status: 'two_factor_required'; challengeToken: string };

type AuthContextValue = {
  user: CurrentUser | null;
  isLoading: boolean;
  login: (input: { email: string; password: string }) => Promise<LoginResult>;
  verifyTwoFactor: (input: { challengeToken: string; code: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const result = await api.auth.me({});
        return result.user;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) => api.auth.login(input),
    onSuccess: (result) => {
      // A two-factor challenge is not a session yet, so nothing is cached.
      if (result.status === 'success') {
        queryClient.setQueryData(['auth', 'me'], result.user);
      }
    },
  });

  const twoFactorMutation = useMutation({
    mutationFn: (input: { challengeToken: string; code: string }) =>
      api.auth.verifyTwoFactor(input),
    onSuccess: (result) => {
      queryClient.setQueryData(['auth', 'me'], result.user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.auth.logout({}),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      void queryClient.invalidateQueries();
    },
  });

  const user = data ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      login: async (input) => {
        const result = await loginMutation.mutateAsync(input);
        return result.status === 'success'
          ? { status: 'success' }
          : { status: 'two_factor_required', challengeToken: result.challengeToken };
      },
      verifyTwoFactor: async (input) => {
        await twoFactorMutation.mutateAsync(input);
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      },
      can: (permission) => user?.permissions.includes(permission) ?? false,
    }),
    [user, isLoading, loginMutation, twoFactorMutation, logoutMutation, queryClient],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
